import React, { forwardRef } from 'react';

type CardVariant = 'default' | 'elevated' | 'premium' | 'gradient-border' | 'stats';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const variantStyles: Record<CardVariant, string> = {
  default: `
    bg-surface-base
    border border-gray-200 dark:border-gray-700
    shadow-sm
    hover:shadow-lg hover:-translate-y-0.5
  `,
  elevated: `
    bg-surface-base
    shadow-md
    border-none
    hover:shadow-xl hover:-translate-y-1
  `,
  premium: `
    bg-gradient-to-br from-primary-50 to-surface-base
    dark:from-primary-900/20 dark:to-surface-base
    border-2 border-primary-200 dark:border-primary-800
    shadow-primary
    hover:shadow-primary hover:shadow-xl hover:border-primary-300 dark:hover:border-primary-700
  `,
  'gradient-border': `
    bg-surface-base
    border-2 border-transparent
    bg-clip-padding
    shadow-sm
    relative
    before:absolute before:inset-0 before:-z-10 before:rounded-xl before:p-[2px]
    before:bg-gradient-to-br before:from-primary-400 before:to-accent-400
    hover:shadow-lg hover:-translate-y-0.5
  `,
  stats: `
    bg-gradient-to-br from-surface-base to-gray-50
    dark:from-surface-base dark:to-gray-800
    border border-gray-200 dark:border-gray-700
    relative overflow-hidden
    hover:shadow-lg hover:-translate-y-0.5 hover:border-primary-300 dark:hover:border-primary-700
  `,
};

const paddingStyles: Record<string, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', hover = true, padding = 'md', className = '', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`
          rounded-xl
          transition-all duration-normal ease-out
          ${variantStyles[variant]}
          ${paddingStyles[padding]}
          ${!hover ? 'hover:transform-none hover:shadow-sm' : ''}
          ${className}
        `.replace(/\s+/g, ' ').trim()}
        {...props}
      >
        {variant === 'stats' && (
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary-100 to-transparent dark:from-primary-900/20 opacity-50 pointer-events-none rounded-full -translate-y-8 translate-x-8" />
        )}
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header Component
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className = '',
  children,
  ...props
}) => (
  <div
    className={`flex items-start justify-between gap-4 mb-4 ${className}`}
    {...props}
  >
    {children || (
      <>
        <div>
          {title && (
            <h3 className="font-display text-xl font-semibold text-text-primary">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </>
    )}
  </div>
);

// Card Body Component
export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = '',
  children,
  ...props
}) => (
  <div className={`${className}`} {...props}>
    {children}
  </div>
);

// Card Footer Component
export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = '',
  children,
  ...props
}) => (
  <div
    className={`mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Stats Card Component
interface StatsCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  trend?: {
    value: string;
    positive: boolean;
  };
  caption?: string;
  icon?: React.ReactNode;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  label,
  value,
  trend,
  caption,
  icon,
  className = '',
  ...props
}) => (
  <Card variant="stats" className={className} {...props}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-text-secondary">{label}</p>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-text-primary">{value}</span>
          {trend && (
            <span
              className={`inline-flex items-center gap-1 text-sm font-medium ${
                trend.positive ? 'text-success-600' : 'text-error-600'
              }`}
            >
              {trend.positive ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              )}
              {trend.value}
            </span>
          )}
        </div>
        {caption && (
          <p className="mt-1 text-xs text-text-tertiary">{caption}</p>
        )}
      </div>
      {icon && (
        <div className="flex-shrink-0 p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          {icon}
        </div>
      )}
    </div>
  </Card>
);

// Meeting/Contract Card with colored header
interface ColoredHeaderCardProps extends Omit<CardProps, 'variant'> {
  headerColor?: 'primary' | 'blue' | 'green' | 'purple' | 'orange' | 'red';
}

const headerColorStyles: Record<string, string> = {
  primary: 'bg-gradient-to-r from-primary-500 to-primary-400',
  blue: 'bg-gradient-to-r from-blue-500 to-blue-400',
  green: 'bg-gradient-to-r from-green-500 to-green-400',
  purple: 'bg-gradient-to-r from-purple-500 to-purple-400',
  orange: 'bg-gradient-to-r from-orange-500 to-orange-400',
  red: 'bg-gradient-to-r from-red-500 to-red-400',
};

export const ColoredHeaderCard: React.FC<ColoredHeaderCardProps> = ({
  headerColor = 'primary',
  padding = 'none',
  className = '',
  children,
  ...props
}) => (
  <Card variant="default" padding={padding} className={`overflow-hidden ${className}`} {...props}>
    <div className={`h-2 ${headerColorStyles[headerColor]}`} />
    <div className="p-6">{children}</div>
  </Card>
);

export default Card;
